import { useState, useEffect, useRef } from "react";
import {
    Box,
    Typography,
    Button,
    Divider,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    // CircularProgress
} from "@mui/material";
// import { LoadingButton } from '@mui/lab'
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import PlayCircleOutlineOutlinedIcon from "@mui/icons-material/PlayCircleOutlineOutlined";
import ArrowBackSharpIcon from "@mui/icons-material/ArrowBackSharp";
import EmptyTestsIcon from "../../assets/iconComponents/EmptyTestsIcon";
import PageService from "../../services/PageService";
import TestCasesCharts from "./TestCasesCharts";
import StatsSummary from "../StatsSummary";
import { socketEventEmitter } from "../../socket";
import SortingIcon from "../SortingIcon";
import TestCasesListItem from "./TestCasesListItem";
import SearchBar from "./SearchBar";
import PaginationComponent from "../PaginationComponent";
import DeleteTestCasesConfirmationModal from "../modals/DeleteTestCasesConfirmationModal";
import EditTestCaseModal from "../common/EditTestCaseModal";
import { sortTableItems } from "../../utils/sortTableItems";
import DetailsAccordion from "../testCase/DetailsAccordion";
import StepsAccordion from "../testCase/StepsAccordion";
import RunsAccordion from "../testCase/RunsAccordion";
import EmbeddedTestRun from "../testCase/EmbeddedTestRun";

const TestDetails = ({ activeItem, setActiveItem, testCases = [], setTestCasesToggler }) => {
    const [allTestCases, setAllTestCases] = useState([]);
    const [draftTestCases, setDraftTestCases] = useState([]);
    const [testCasesTotal, setTestCasesTotal] = useState(0);
    const [checkedTestCases, setCheckedTestCases] = useState([]);
    const [testCasesToDelete, setTestCasesToDelete] = useState([]);
    const [testCaseToEdit, setTestCaseToEdit] = useState({ id: "", name: "" });
    // const [isHeaderCheckboxChecked, setIsHeaderCheckboxChecked] =
    //     useState(false);
    const [sortConfig, setSortConfig] = useState({
        key: "created_at",
        direction: "desc",
    });
    const [page, setPage] = useState(1);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteContext, setDeleteContext] = useState({ mode: "blueprint" });
    const [selectedPage, setSelectedPage] = useState("");
    const [editTestCaseOpen, setEditTestCaseOpen] = useState(false);
    const [tabToggler, setTabToggler] = useState(false);
    const [selectedTestCase, setSelectedTestCase] = useState(null);
    const [selectedTestRuns, setSelectedTestRuns] = useState([]);
    const [lastFolderItem, setLastFolderItem] = useState(null);
    const [uploadingSteps, setUploadingSteps] = useState({});
    const [viewMode, setViewMode] = useState("details");
    const [selectedRunId, setSelectedRunId] = useState(null);
    const [selectedRunDetails, setSelectedRunDetails] = useState(null);
    const [runDetailsLoading, setRunDetailsLoading] = useState(false);
    const [runOpenedFromDetails, setRunOpenedFromDetails] = useState(false);
    const autoOpenKeyRef = useRef(null);
    const detailsContainerRef = useRef(null);

    const statusStyles = {
        RUNNING: { backgroundColor: "#4574D2", color: "#fff" },
        COMPLETED: { backgroundColor: "#198754", color: "#fff" },
        FAILED: { backgroundColor: "#D32F2F", color: "#fff" },
        INITIAL_FAILURE: { backgroundColor: "#8E1515", color: "#fff" },
        Draft: { backgroundColor: "#6D6E6F", color: "#fff" },
    };

    const normalizeId = (value) =>
        value === undefined || value === null ? "" : String(value);

    const getFolderTestCasesFromHierarchy = () => {
        if (
            Array.isArray(activeItem?.testCases) &&
            activeItem.testCases.length
        ) {
            return activeItem.testCases;
        }
        if (!Array.isArray(testCases) || !activeItem?.id) return [];
        const folderId = normalizeId(activeItem.pageId || activeItem.id);
        const isVersionMatch = (versionId) =>
            !activeItem?.versionId ||
            normalizeId(versionId) === normalizeId(activeItem.versionId);
        const isCycleMatch = (cycleId) =>
            !activeItem?.cycleId ||
            normalizeId(cycleId) === normalizeId(activeItem.cycleId);

        for (const version of testCases) {
            if (!isVersionMatch(version.id)) continue;
            for (const cycle of version.cycles || []) {
                if (!isCycleMatch(cycle.id)) continue;
                for (const folder of cycle.folders || []) {
                    if (normalizeId(folder.id) === folderId) {
                        return (
                            folder.testCases ||
                            folder.test_cases ||
                            []
                        );
                    }
                }
            }
        }

        return [];
    };

    const testCasesPerPage = 10;

    const sortedTestCases = sortTableItems(allTestCases, sortConfig);

    const filteredTestCases = selectedPage
        ? sortedTestCases.filter((testCase) =>
            testCase.name.toLowerCase().includes(selectedPage.toLowerCase())
        )
        : sortedTestCases;

    const paginatedTestCases = filteredTestCases.slice(
        (page - 1) * testCasesPerPage,
        page * testCasesPerPage
    );

    const totalPages = Math.ceil(filteredTestCases.length / testCasesPerPage);

    useEffect(() => {
        autoOpenKeyRef.current = null;
        if (activeItem?.type === "folder") {
            const pageId = activeItem.pageId || activeItem.id;
            setLastFolderItem({
                ...activeItem,
                type: "folder",
                id: pageId || activeItem.id,
                pageId,
            });
            setViewMode("details");
            setSelectedRunId(null);
            setSelectedRunDetails(null);
        } else if (activeItem?.type === "testCase" && activeItem?.parentItem) {
            const pageId =
                activeItem.parentItem.pageId || activeItem.parentItem.id;
            setLastFolderItem({
                ...activeItem.parentItem,
                type: "folder",
                id: pageId || activeItem.parentItem.id,
                pageId,
            });
            setViewMode(activeItem?.cycleId ? "runDetails" : "details");
            setSelectedRunId(null);
            setSelectedRunDetails(null);
        } else if (!activeItem) {
            setLastFolderItem(null);
            setViewMode("details");
            setSelectedRunId(null);
            setSelectedRunDetails(null);
            autoOpenKeyRef.current = null;
        }
    }, [activeItem]);

    useEffect(() => {
        setUploadingSteps({});
        const getAllTestCases = async () => {
            try {
                if (activeItem?.type === "folder" && activeItem?.id) {
                    const pageId = activeItem.pageId || activeItem.id;
                    const normalizedPageId = normalizeId(pageId);
                    const isJourneyFolder = activeItem?.folderType === "journey";
                    const folderTestCases = getFolderTestCasesFromHierarchy();
                    const filterByPage = (items = []) =>
                        activeItem?.isBlueprint || !normalizedPageId
                            ? items
                            : items.filter(
                                (testCase) => {
                                    const testPageId = normalizeId(
                                        testCase.pageId || testCase.page_id
                                    );
                                    const testJourneyId = normalizeId(
                                        testCase.journeyId ||
                                        testCase.journey_id
                                    );
                                    if (testPageId === normalizedPageId) {
                                        return true;
                                    }
                                    if (
                                        isJourneyFolder &&
                                        testJourneyId === normalizedPageId
                                    ) {
                                        return true;
                                    }
                                    return false;
                                }
                            );

                    const shouldUseHierarchy =
                        activeItem?.isBlueprint ||
                        (!activeItem?.cycleId && folderTestCases.length);

                    if (shouldUseHierarchy && folderTestCases.length) {
                        const filteredHierarchyCases =
                            filterByPage(folderTestCases);
                        setAllTestCases(filteredHierarchyCases);
                        setTestCasesTotal(filteredHierarchyCases.length);
                        setDraftTestCases(
                            filteredHierarchyCases
                                .filter(
                                    (testCase) => testCase.status === "Draft"
                                )
                                .map((testCase) => testCase.id)
                        );
                    } else if (activeItem?.cycleId) {
                        const cycleCases =
                            await PageService.getCyclePageTestCases(
                                activeItem.cycleId,
                                pageId
                            );
                        const filteredCycleCases = filterByPage(
                            Array.isArray(cycleCases)
                                ? cycleCases
                                : cycleCases?.testCases || []
                        );
                        setAllTestCases(filteredCycleCases);
                        setTestCasesTotal(filteredCycleCases.length);
                        setDraftTestCases(
                            filteredCycleCases
                                .filter((testCase) => testCase.status === "Draft")
                                .map((testCase) => testCase.id)
                        );
                    } else {
                        const testCasesData =
                            await PageService.getAllTestCasesByPageId(pageId);
                        const filteredTestCases = filterByPage(
                            testCasesData.testCases || []
                        );
                        setAllTestCases(filteredTestCases);
                        setTestCasesTotal(filteredTestCases.length);
                        setDraftTestCases(
                            filteredTestCases
                                .filter((testCase) => testCase.status === "Draft")
                                .map((testCase) => testCase.id)
                        );
                    }
                    setSelectedTestCase(null);
                    setSelectedTestRuns([]);
                    setViewMode("details");
                    setSelectedRunId(null);
                    setSelectedRunDetails(null);
                } else if (activeItem?.type === "testCase" && activeItem?.id) {
                    const testCase = await PageService.getTestCaseById(
                        activeItem.id
                    );
                    const testRunsData =
                        await PageService.getAllTestRunsByTestCaseId(
                            activeItem.id
                        );
                    const runs = testRunsData?.runs || [];
                    const isCycleContext = Boolean(activeItem?.cycleId);
                    setSelectedTestCase({
                        ...testCase,
                        versions: testCase?.versions || [],
                    });
                    setSelectedTestRuns(runs);
                    setAllTestCases([]);
                    setTestCasesTotal(0);
                    setDraftTestCases([]);
                    setSelectedRunDetails(null);
                    if (isCycleContext) {
                        if (activeItem?.openInDetails) {
                            setViewMode("details");
                        } else {
                            setViewMode("runDetails");
                        }
                    }
                } else {
                    setAllTestCases([]);
                    setTestCasesTotal(0);
                    setDraftTestCases([]);
                    setSelectedTestCase(null);
                    setSelectedTestRuns([]);
                    setViewMode("details");
                    setSelectedRunId(null);
                    setSelectedRunDetails(null);
                }
            } catch (error) {
                console.error("Error fetching test cases:", error);
                setAllTestCases([]);
                setTestCasesTotal(0);
                setDraftTestCases([]);
                setSelectedTestCase(null);
                setSelectedTestRuns([]);
            }
        };

        if (activeItem) {
            getAllTestCases();
            if (detailsContainerRef.current) {
                detailsContainerRef.current.scrollTop = 0;
            }
        }

        const handleTaskCompleted = async (data) => {
            if (
                ((activeItem?.type === "folder" &&
                    data.pageId === activeItem?.id &&
                    (data.type === "Test Case" ||
                        data.type === "Discover Test Cases")) ||
                    (activeItem?.type === "testCase" &&
                        data.testCaseId === activeItem?.id))
            ) {
                setTabToggler((prev) => !prev);
            }
        };

        const handleTaskFailed = async (data) => {
            if (
                ((activeItem?.type === "folder" &&
                    data.pageId === activeItem?.id &&
                    (data.type === "Test Case" ||
                        data.type === "Discover Test Cases")) ||
                    (activeItem?.type === "testCase" &&
                        data.testCaseId === activeItem?.id))
            ) {
                setTabToggler((prev) => !prev);
            }
        };

        socketEventEmitter.on("taskCompleted", handleTaskCompleted);
        socketEventEmitter.on("taskFailed", handleTaskFailed);

        return () => {
            socketEventEmitter.off("taskCompleted", handleTaskCompleted);
            socketEventEmitter.off("taskFailed", handleTaskFailed);
        };
    }, [activeItem, tabToggler, testCases]);

    // When opening a test case inside a cycle directly from the hierarchy, auto-open the most recent test run
    useEffect(() => {
        if (
            activeItem?.type !== "testCase" ||
            !activeItem?.cycleId ||
            activeItem?.openInDetails ||
            !selectedTestCase ||
            !Array.isArray(selectedTestRuns)
        ) {
            return;
        }
        const cycleId = activeItem.cycleId;
        if (selectedTestRuns.length === 0) return;
        const getCycleId = (run) =>
            run?.testCycleId ||
            run?.test_cycle_id ||
            run?.cycleId ||
            run?.cycle_id ||
            run?.cycle?.id ||
            run?.testCycle?.id;
        const cycleRuns = selectedTestRuns.filter((run) => {
            const rid = normalizeId(getCycleId(run));
            return rid && rid === normalizeId(cycleId);
        });
        if (cycleRuns.length === 0) return;
        const latestCycleRun = [...cycleRuns].sort(
            (a, b) =>
                new Date(
                    b.updated_at || b.updatedAt || b.started_at || b.startedAt || 0
                ) -
                new Date(
                    a.updated_at || a.updatedAt || a.started_at || a.startedAt || 0
                )
        )[0];
        if (!latestCycleRun?.id) return;
        const key = `${activeItem.id}-${cycleId}-${latestCycleRun.id}`;
        if (autoOpenKeyRef.current === key) return;
        autoOpenKeyRef.current = key;
        setViewMode("runDetails");
        setRunDetailsLoading(true);
        setSelectedRunId(latestCycleRun.id);
        setSelectedRunDetails(null);
        let cancelled = false;
        PageService.getTestRunById(latestCycleRun.id)
            .then((runDetails) => {
                if (!cancelled) setSelectedRunDetails(runDetails);
            })
            .catch((err) => {
                if (!cancelled) {
                    console.error("Error fetching test run:", err);
                    setSelectedRunId(null);
                }
            })
            .finally(() => {
                if (!cancelled) setRunDetailsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [
        activeItem?.type,
        activeItem?.id,
        activeItem?.cycleId,
        selectedTestCase?.id,
        selectedTestRuns,
    ]);

    const handleRunTestCases = async () => {
        try {
            const options =
                activeItem?.cycleId
                    ? {
                        originType: "cycle",
                        testCycleId: activeItem.cycleId,
                        projectVersionId: activeItem.versionId,
                    }
                    : {};
            await PageService.runTestCases(checkedTestCases, options);
            setCheckedTestCases([]);
        } catch (error) {
            console.log(error);
        }
        setTabToggler((prev) => !prev);
    };

    const buildDeleteContext = (ids = []) => {
        if (!activeItem?.cycleId) {
            return { mode: "blueprint" };
        }

        const selectedCases = allTestCases.filter((tc) =>
            ids.includes(tc.id)
        );
        const cycleItemIds = selectedCases
            .map((tc) => tc.cycleItemId || tc.cycle_item_id)
            .filter(Boolean);
        const pageIds = selectedCases
            .map(
                (tc) =>
                    tc.pageId ||
                    tc.page_id ||
                    tc.journeyId ||
                    tc.journey_id ||
                    activeItem?.pageId ||
                    activeItem?.id
            )
            .filter(Boolean);

        return {
            mode: "cycle",
            cycleId: activeItem.cycleId,
            cycleItemIds,
            pageIds,
        };
    };

    const handleOpenDeleteDialog = (testCaseID) => {
        const ids = testCaseID ? [testCaseID] : checkedTestCases;
        setTestCasesToDelete(ids);
        setDeleteContext(buildDeleteContext(ids));
        setDeleteOpen(true);
    };

    const handleDeleteSuccess = (deletedIds = []) => {
        const idsSet = new Set(deletedIds);
        setAllTestCases((prev) => {
            const next = prev.filter((tc) => !idsSet.has(tc.id));
            setTestCasesTotal(next.length);
            return next;
        });
        setDraftTestCases((prev) => prev.filter((id) => !idsSet.has(id)));
        if (selectedTestCase && idsSet.has(selectedTestCase.id)) {
            setSelectedTestCase(null);
            setSelectedTestRuns([]);
            setViewMode("details");
            setSelectedRunId(null);
            setSelectedRunDetails(null);
        }
    };

    const handlePageChange = (event, value) => {
        setPage(value);
    };

    const handleCheckboxChange = (id) => {
        setCheckedTestCases((prevCheckedTestCases) => {
            if (prevCheckedTestCases.includes(id)) {
                return prevCheckedTestCases.filter(
                    (testCaseId) => testCaseId !== id
                );
            } else {
                return [...prevCheckedTestCases, id];
            }
        });
    };

    const handleSortChange = (key) => {
        setSortConfig((prevConfig) => ({
            key,
            direction:
                prevConfig.key === key && prevConfig.direction === "asc"
                    ? "desc"
                    : "asc",
        }));
    };

    const handleSearch = (event, newValue) => {
        if (!newValue) {
            setSelectedPage("");
            setPage(1);
            return;
        }
        setSelectedPage(newValue?.name || "");
        setPage(1);
    };

    const handleUpdateStepValue = async (id, newValue) => {
        try {
            await PageService.updateTestCaseStepValue(id, newValue);
            setTabToggler((prev) => !prev);
        } catch (error) {
            console.error("Error updating test case step value:", error);
        }
    };

    const handleUploadStepFile = async (stepId, file) => {
        setUploadingSteps((prev) => ({ ...prev, [stepId]: true }));
        try {
            await PageService.uploadTestCaseStepFile(stepId, file);
            setTabToggler((prev) => !prev);
        } catch (error) {
            console.error("Error uploading file for test case step:", error);
        } finally {
            setUploadingSteps((prev) => {
                const next = { ...prev };
                delete next[stepId];
                return next;
            });
        }
    };

    const renderStatusBadge = (status) => {
        if (!status) return null;
        let label = status;
        if (status === "RUNNING") label = "In Progress...";
        else if (status === "COMPLETED") label = "Passed";
        else if (status === "INITIAL_FAILURE") label = "Initial failure";
        else label = status.toLowerCase();

        return (
            <Box
                sx={{
                    ...statusStyles[status],
                    px: 1.5,
                    py: 0.5,
                    borderRadius: "8px",
                    fontSize: "12px",
                    fontWeight: 600,
                    textTransform: "capitalize",
                }}
            >
                {label}
            </Box>
        );
    };

    const handleBackToList = () => {
        if (!setActiveItem) return;

        if (lastFolderItem?.pageId || lastFolderItem?.id) {
            const pageId = lastFolderItem.pageId || lastFolderItem.id;
            setActiveItem({
                ...lastFolderItem,
                type: "folder",
                id: pageId,
                pageId,
            });
            return;
        }

        if (selectedTestCase?.pageId) {
            setActiveItem({
                type: "folder",
                id: selectedTestCase.pageId,
                pageId: selectedTestCase.pageId,
            });
        } else {
            setActiveItem(null);
        }
    };

    const handlePreviewTestCase = (testCase) => {
        if (!setActiveItem || !testCase?.id) return;
        const pageId =
            activeItem?.pageId ||
            activeItem?.id ||
            testCase.pageId ||
            testCase.page_id ||
            "";
        const cycleId =
            activeItem?.cycleId ||
            testCase?.cycleId ||
            testCase?.testCycleId ||
            testCase?.test_cycle_id ||
            "";
        setActiveItem({
            type: "testCase",
            id: testCase.id,
            cycleId,
            cycleItemId: testCase?.cycleItemId,
            pageId,
            versionId: activeItem?.versionId || testCase?.versionId,
            parentItem: {
                type: "folder",
                id: pageId,
                pageId,
                cycleId,
                versionId: activeItem?.versionId,
                isBlueprint: activeItem?.isBlueprint,
            },
            isBlueprint: activeItem?.isBlueprint,
            openInDetails: true,
        });
        setViewMode("details");
        setSelectedTestRuns([]);
        setSelectedRunId(null);
        setSelectedRunDetails(null);
        autoOpenKeyRef.current = null;
        setRunOpenedFromDetails(false);
    };

    const handleBackFromRuns = () => {
        setViewMode("details");
        setSelectedRunId(null);
        setSelectedRunDetails(null);
        setRunDetailsLoading(false);
        setRunOpenedFromDetails(false);
    };

    const handleOpenRunDetails = async (run) => {
        const runId = typeof run === "object" ? run?.id : run;
        if (!runId) return false;

        try {
            // This handler is only used when user selects a run from the details view (e.g. RunsAccordion)
            setRunOpenedFromDetails(true);
            setViewMode("runDetails");
            setRunDetailsLoading(true);
            setSelectedRunId(runId);
            setSelectedRunDetails(null);
            const runDetails = await PageService.getTestRunById(runId);
            setSelectedRunDetails(runDetails);
            return true;
        } catch (error) {
            console.error("Error fetching test run:", error);
            if (activeItem?.cycleId) {
                setViewMode("runDetails");
            } else {
                setViewMode("details");
            }
            setSelectedRunId(null);
            setSelectedRunDetails(null);
            return false;
        } finally {
            setRunDetailsLoading(false);
        }
    };

    return (
        <Box className="testCasesDetails" ref={detailsContainerRef}>
            {activeItem?.type === "testCase" ? (
                <Box className="testCasesDetailsContent">
                    {!(viewMode === "runDetails" && !activeItem?.cycleId) && (
                        <Box
                            className="testCasesDetailsTitleContainer"
                            sx={{
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: 2,
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                {setActiveItem ? (
                                    <IconButton
                                        size="small"
                                        onClick={
                                            viewMode === "runDetails" && activeItem?.cycleId
                                                ? (runOpenedFromDetails ? handleBackFromRuns : handleBackToList)
                                                : viewMode === "runDetails"
                                                    ? handleBackFromRuns
                                                    : handleBackToList
                                        }
                                        sx={{
                                            width: 32,
                                            height: 32,
                                            border: "1px solid #2D4356",
                                            backgroundColor: "#fff",
                                            color: "#2D4356",
                                            "&:hover": {
                                                backgroundColor: "#f5f7fb",
                                            },
                                        }}
                                    >
                                        <ArrowBackSharpIcon fontSize="small" />
                                    </IconButton>
                                ) : null}

                                <Typography className="testCasesDetailsTitle">
                                    {viewMode === "runDetails" &&
                                        selectedRunDetails?.name
                                        ? selectedRunDetails.name
                                        : selectedTestCase?.testCaseName ||
                                        selectedTestCase?.name ||
                                        "Test Case"}
                                </Typography>

                                {renderStatusBadge(
                                    activeItem?.cycleId && (!selectedTestRuns || selectedTestRuns.length === 0)
                                        ? "Draft"
                                        : viewMode === "runDetails"
                                            ? selectedRunDetails?.status
                                            : selectedTestCase?.status
                                )}
                            </Box>

                            {activeItem?.cycleId && viewMode === "runDetails" && selectedRunDetails?.id ? (
                                <Box sx={{ px: 2, display: "flex", justifyContent: "flex-start" }}>
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        size="small"
                                        onClick={() =>
                                            window.open(
                                                `${window.location.origin}/test-run/${selectedRunDetails.id}`,
                                                "_blank",
                                                "noopener,noreferrer"
                                            )
                                        }
                                        sx={{ textTransform: 'uppercase' }}
                                    >
                                        Open in new tab
                                    </Button>
                                </Box>
                            ) : null}
                        </Box>
                    )}


                    {selectedTestCase ? (
                        viewMode === "runDetails" ? (
                            <EmbeddedTestRun
                                key={selectedRunId || "run-details"}
                                testRun={selectedRunDetails}
                                testCase={selectedTestCase}
                                loading={runDetailsLoading}
                                onBack={
                                    activeItem?.cycleId
                                        ? (runOpenedFromDetails ? handleBackFromRuns : handleBackToList)
                                        : handleBackFromRuns
                                }
                                onOpenInNewTab={
                                    activeItem?.cycleId
                                        ? null
                                        : (runId) =>
                                            window.open(
                                                `${window.location.origin}/test-run/${runId}`,
                                                "_blank",
                                                "noopener,noreferrer"
                                            )
                                }
                                hideHeader={!!activeItem?.cycleId}
                                isDraft={!!activeItem?.cycleId && (!selectedTestRuns || selectedTestRuns.length === 0)}
                            />
                        ) : (
                            <>
                                <DetailsAccordion
                                    testCase={selectedTestCase}
                                    testRuns={selectedTestRuns}
                                    compact
                                />
                                <StepsAccordion
                                    testCase={selectedTestCase}
                                    handleUpdateStepValue={handleUpdateStepValue}
                                    handleUploadStepFile={handleUploadStepFile}
                                    uploadingSteps={uploadingSteps}
                                    forceEditable={!!activeItem?.cycleId}
                                />
                                <RunsAccordion
                                    testCase={selectedTestCase}
                                    testRuns={selectedTestRuns}
                                    embedded
                                    onRunSelect={handleOpenRunDetails}
                                />
                            </>
                        )
                    ) : (
                        <Box className="testCasesEmptyContainer">
                            <Box className="testCasesEmpty">
                                <Box className="testCasesEmptyIconContainer">
                                    <EmptyTestsIcon />
                                </Box>
                                <Typography className="testCasesEmptyLabel">
                                    Loading test case...
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Box>
            ) : activeItem && activeItem.type === "folder" ? (
                <>
                    <Box className="testCasesDetailsTitleContainer">
                        <Typography className="testCasesDetailsTitle">
                            Details
                        </Typography>
                    </Box>
                    <TestCasesCharts
                        testCasesData={allTestCases}
                        testCasesTotal={testCasesTotal}
                        draftTestCases={draftTestCases}
                    />
                    <Divider />
                    <Box className="testCasesDetailsStatsContainer">
                        <StatsSummary
                            title="Total Test Cases"
                            value={testCasesTotal}
                            color="primary"
                        />
                        <StatsSummary
                            title="Total Run Time"
                            value="3 minutes"
                            color="primary"
                        />
                    </Box>
                    <Box className="testCasesDetailsTableContainer">
                        <Box className="testCasesDetailsTableActionsContainer">
                            <Box className="testCasesListSettingsContainer">
                                <Box className="testCasesListItemSettings">
                                    {checkedTestCases.length ? (
                                        <>
                                            <Button
                                                className="runSelectedButton"
                                                variant="contained"
                                                color="secondary"
                                                onClick={handleRunTestCases}
                                            >
                                                <PlayCircleOutlineOutlinedIcon
                                                    className="runSelectedButtonIcon"
                                                    color="#fff"
                                                />
                                                RUN SELECTED
                                            </Button>
                                            <Button
                                                className="deleteButton"
                                                variant="outlined"
                                                color="error"
                                                onClick={() =>
                                                    handleOpenDeleteDialog()
                                                }
                                            >
                                                <DeleteOutlineOutlinedIcon
                                                    className="deleteButtonIcon"
                                                    color="error"
                                                />
                                                DELETE
                                            </Button>
                                        </>
                                    ) : null}
                                </Box>
                                <Box className="testCasesListCommonSettings">
                                    <SearchBar
                                        testCasesList={allTestCases}
                                        handleSearch={handleSearch}
                                    />
                                </Box>
                            </Box>
                        </Box>
                        <TableContainer
                            className="testCasesTableContainer"
                            component={Paper}
                        >
                            <Table className="testCasesTable" stickyHeader>
                                <TableHead className="testCasesTableHeader">
                                    <TableRow>
                                        <TableCell
                                            className="testCasesTableHeaderCheckboxCell"
                                            padding="checkbox"
                                            sx={{ width: 28, minWidth: 28 }}
                                        />
                                        <TableCell
                                            className="testCasesTableHeaderCell testCaseId"
                                            onClick={() =>
                                                handleSortChange("public_id")
                                            }
                                        >
                                            <Box className="testCasesTableHeaderCellContent">
                                                <SortingIcon
                                                    sortConfig={sortConfig}
                                                    currentKey={"public_id"}
                                                />
                                                Test Case ID
                                            </Box>
                                        </TableCell>
                                        <TableCell
                                            className="testCasesTableHeaderCell testCaseName"
                                            onClick={() =>
                                                handleSortChange("name")
                                            }
                                        >
                                            <Box className="testCasesTableHeaderCellContent">
                                                <SortingIcon
                                                    sortConfig={sortConfig}
                                                    currentKey={"name"}
                                                />
                                                Test Case Name
                                            </Box>
                                        </TableCell>
                                        <TableCell
                                            className="testCasesTableHeaderCell"
                                            onClick={() =>
                                                handleSortChange("created_at")
                                            }
                                        >
                                            <Box className="testCasesTableHeaderCellContent">
                                                <SortingIcon
                                                    sortConfig={sortConfig}
                                                    currentKey={"created_at"}
                                                />
                                                Created On
                                            </Box>
                                        </TableCell>
                                        <TableCell
                                            className="testCasesTableHeaderCell"
                                            onClick={() =>
                                                handleSortChange(
                                                    "regenerated_at"
                                                )
                                            }
                                        >
                                            <Box className="testCasesTableHeaderCellContent">
                                                <SortingIcon
                                                    sortConfig={sortConfig}
                                                    currentKey={
                                                        "regenerated_at"
                                                    }
                                                />
                                                Last Modified
                                            </Box>
                                        </TableCell>
                                        <TableCell
                                            className="testCasesTableHeaderCell"
                                            onClick={() =>
                                                handleSortChange("created_by")
                                            }
                                        >
                                            <Box className="testCasesTableHeaderCellContent">
                                                <SortingIcon
                                                    sortConfig={sortConfig}
                                                    currentKey={"created_by"}
                                                />
                                                Created by
                                            </Box>
                                        </TableCell>
                                        <TableCell
                                            className="testCasesTableHeaderCell"
                                            onClick={() =>
                                                handleSortChange("status")
                                            }
                                        >
                                            <Box className="testCasesTableHeaderCellContent">
                                                <SortingIcon
                                                    sortConfig={sortConfig}
                                                    currentKey={"status"}
                                                />
                                                Status
                                            </Box>
                                        </TableCell>
                                        <TableCell className="testCasesTableHeaderCellContent testCasesActions">
                                            Actions
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginatedTestCases.length ? (
                                        paginatedTestCases.map((testCase) => (
                                            <TestCasesListItem
                                                key={testCase.id}
                                                testCase={testCase}
                                                cycleContext={
                                                    activeItem?.cycleId
                                                        ? {
                                                            cycleId:
                                                                activeItem.cycleId,
                                                            versionId:
                                                                activeItem.versionId,
                                                        }
                                                        : null
                                                }
                                                setTestCaseToEdit={
                                                    setTestCaseToEdit
                                                }
                                                setEditTestCaseOpen={
                                                    setEditTestCaseOpen
                                                }
                                                checkedTestCases={
                                                    checkedTestCases
                                                }
                                                handleCheckboxChange={
                                                    handleCheckboxChange
                                                }
                                                handleOpenDeleteDialog={
                                                    handleOpenDeleteDialog
                                                }
                                                tabToggler={tabToggler}
                                                setTabToggler={setTabToggler}
                                                onPreviewTestCase={
                                                    handlePreviewTestCase
                                                }
                                            />
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell
                                                colSpan={8}
                                                className="testCasesTableEmptyByFilters"
                                            >
                                                <Typography
                                                    variant="body1"
                                                    className="testCasesTableEmptyByFiltersText"
                                                >
                                                    No test cases found for the
                                                    current filters.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Box mr={2}>
                            <PaginationComponent
                                page={page}
                                totalItems={filteredTestCases.length}
                                itemsPerPage={testCasesPerPage}
                                count={totalPages}
                                handlePageChange={handlePageChange}
                            />
                        </Box>
                        <DeleteTestCasesConfirmationModal
                            deleteOpen={deleteOpen}
                            setDeleteOpen={setDeleteOpen}
                            tabToggler={tabToggler}
                            setTabToggler={setTabToggler}
                            testCasesToDelete={testCasesToDelete}
                            setCheckedTestCases={setCheckedTestCases}
                            refreshHierarchy={
                                typeof setTestCasesToggler === "function"
                                    ? () =>
                                        setTestCasesToggler(
                                            (prev) => !prev
                                        )
                                    : undefined
                            }
                            onAfterDelete={handleDeleteSuccess}
                            deleteContext={deleteContext}
                        />
                        {editTestCaseOpen && (
                            <EditTestCaseModal
                                open={editTestCaseOpen}
                                onClose={() => setEditTestCaseOpen(false)}
                                testCase={testCaseToEdit}
                                onSuccess={() => setTabToggler(!tabToggler)}
                                showDelete
                            />
                        )}
                    </Box>
                </>
            ) : (
                <Box className="testCasesEmptyContainer">
                    <Box className="testCasesEmpty">
                        <Box className="testCasesEmptyIconContainer">
                            <EmptyTestsIcon />
                        </Box>
                        <Typography className="testCasesEmptyLabel">
                            Test Case Not Selected
                        </Typography>
                        <Typography className="testCasesEmptyText">
                            Select a folder to preview test case details
                        </Typography>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default TestDetails;
